import type { IRewardsService } from '../../../services/rewards/IRewardsService';
import type { RestockScheduleEntry } from '../../../services/rewards/types';

export type RestockState = {
  label: string;
  localDate: string;
  loading: boolean;
};

export type RestockListener = (state: RestockState) => void;

const DEFAULT_SCHEDULE: RestockScheduleEntry[] = [
  { dayOfWeek: 0, hour: 12, minute: 0, timezone: 'America/Los_Angeles' },
];

export class RestockManager {
  private state: RestockState = { label: '', localDate: '', loading: true };
  private listeners: RestockListener[] = [];
  private schedule: RestockScheduleEntry[] = DEFAULT_SCHEDULE;
  private tickInterval: ReturnType<typeof setInterval> | null = null;
  private destroyed = false;

  constructor(private readonly rewardsService: IRewardsService) {}

  async init(): Promise<void> {
    try {
      const res = await this.rewardsService.getRestockSchedule();
      if (this.destroyed) return;
      if (res.schedule?.length) {
        this.schedule = res.schedule;
      }
    } catch {
      // Fall back to default schedule
    } finally {
      if (!this.destroyed) {
        this.updateState({ loading: false });
      }
    }
    this.tick();
    this.tickInterval = setInterval(() => this.tick(), 1000);
  }

  destroy(): void {
    this.destroyed = true;
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
    this.listeners = [];
  }

  getState(): RestockState {
    return this.state;
  }

  async refreshSchedule(): Promise<void> {
    try {
      const res = await this.rewardsService.getRestockSchedule();
      if (this.destroyed) return;
      if (res.schedule?.length) {
        this.schedule = res.schedule;
        this.tick();
      }
    } catch {
      // Keep existing schedule on failure
    }
  }

  subscribe(listener: RestockListener): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  private tick(): void {
    const now = new Date();
    const next = this.computeNextRestock(now);
    if (!next) {
      this.updateState({ label: '—', localDate: '' });
      return;
    }
    const ms = next.getTime() - now.getTime();
    this.updateState({
      label: this.formatCountdown(ms),
      localDate: this.formatLocalDate(next),
    });
  }

  private computeNextRestock(now: Date): Date | null {
    if (this.schedule.length === 0) return null;
    let soonest: Date | null = null;
    for (const entry of this.schedule) {
      const target = this.getNextOccurrence(entry, now);
      if (!soonest || target < soonest) {
        soonest = target;
      }
    }
    return soonest;
  }

  private getNextOccurrence(entry: RestockScheduleEntry, now: Date): Date {
    const tz = entry.timezone || 'America/Los_Angeles';
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(now);
    const get = (t: string) => parseInt(parts.find((p) => p.type === t)?.value ?? '0', 10);
    const tzHour = get('hour');
    const tzMinute = get('minute');
    const tzSecond = get('second');

    const dowFormatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' });
    const dowMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const tzDow = dowMap[dowFormatter.format(now)] ?? 0;

    let daysUntil = (entry.dayOfWeek - tzDow + 7) % 7;
    if (daysUntil === 0) {
      const targetMinutes = entry.hour * 60 + entry.minute;
      const currentMinutes = tzHour * 60 + tzMinute + (tzSecond > 0 ? 1 : 0);
      if (currentMinutes >= targetMinutes) daysUntil = 7;
    }

    const currentTotalSecs = tzHour * 3600 + tzMinute * 60 + tzSecond;
    const targetTotalSecs = entry.hour * 3600 + entry.minute * 60;
    const offsetMs = (daysUntil * 86400 + targetTotalSecs - currentTotalSecs) * 1000;
    return new Date(now.getTime() + offsetMs);
  }

  private formatCountdown(ms: number): string {
    if (ms <= 0) return '0d 00:00:00';
    const totalSecs = Math.floor(ms / 1000);
    const days = Math.floor(totalSecs / 86400);
    const hours = Math.floor((totalSecs % 86400) / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${days}d ${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  }

  private formatLocalDate(date: Date): string {
    return date.toLocaleString(undefined, {
      weekday: 'long',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  }

  private updateState(partial: Partial<RestockState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}
